"use client";

import { useEffect, useState, useRef, useCallback } from "react";
import { useIdleTimer } from "../../hooks/useIdleTimer"; // 🧩 Fixed extra space
import { SessionWarningModal } from "../SessionWarningModal/SessionWarningModal";
import { getAuth, signOut } from "firebase/auth";

export const SessionManager = () => {
  const [countdown, setCountdown] = useState(10);
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousWarningState = useRef(false); // 🧩 track previous modal open state

  // 🔐 Firebase localStorage key (used to detect if user is logged in)
  const FIREBASE_AUTH_KEY =
    "firebase:authUser:AIzaSyD3TdRUub4-yb5O17cfQlNsaLYkC_TM4aE:[DEFAULT]";

  // ✅ Check if Firebase auth key exists in localStorage
  const isUserLoggedIn = () => {
    if (typeof window === "undefined") return false;
    return !!localStorage.getItem(FIREBASE_AUTH_KEY);
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // ✅ Logout handler
  const handleLogout = useCallback(async () => {
    if (!isUserLoggedIn()) return;

    // Stop countdown
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Clear tokens and Firebase auth data
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem(FIREBASE_AUTH_KEY);

    const auth = getAuth();
    try {
      await signOut(auth);
      setIsLoggedOut(true);
      // window.location.href = "/login";
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }, []);

  // ⏳ Idle timer hook
  const { isWarningVisible } = useIdleTimer({
    timeout: 15* 60 * 1000, // 15 minute inactivity
    warningTime: 10 * 1000, // show modal 10s before timeout
    onTimeout: handleLogout,
  });

  // ✅ Effect to handle countdown + reset when modal opens again
  useEffect(() => {
    // Stop countdown if user logged out or no Firebase auth data
    if (!isUserLoggedIn() || isLoggedOut) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      setCountdown(10);
      return;
    }

    // ⚠️ Start countdown when warning modal visible
    if (isWarningVisible && !intervalRef.current) {
      setCountdown(10); // ✅ Reset to 10 when modal first opens
      intervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    // ✅ Reset countdown when user becomes active (modal closes)
    if (!isWarningVisible && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setCountdown(10); // Reset countdown for next modal display
    }

    // 🧹 Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isWarningVisible, isLoggedOut, handleLogout]);

  // Hide modal if not logged in or already logged out
  if (!isMounted || isLoggedOut || !isUserLoggedIn()) return null;

  return (
    <SessionWarningModal
      isVisible={isWarningVisible && isUserLoggedIn() && !isLoggedOut}
      countdown={countdown}
    />
  );
};
